import { APP_SECTION_LOOKUP } from '@/config/app-section-configs'
import { deriveScopeChain, type PresenceScope } from '@/lib/presence/scopes'
import { getMatchedSection } from '@/utils/get-matched-section'

export type SectionMatch = {
  title: string
  params: Record<string, string>
  prefix: string
  /**
   * The section-config key that matched, e.g.
   * `/internal/[id]/support/cases/[caseId]` — dynamic segments intact, so it
   * identifies the PAGE rather than the record. Null when the match came from
   * a config with no route key.
   *
   * This is what the activity rollup counts against
   * (`src/lib/activity/config.ts`): keying usage on the route rather than the
   * pathname is what bounds its cardinality to the route configs.
   */
  route: string | null
  /**
   * Presence rooms for this route, broadest first — derived from the matched
   * route's scope params, not declared per route. Empty when the route carries
   * no scope-bearing param (every org dashboard, list page and report) or when
   * it opted out with `presence: false`.
   */
  presenceScopes: PresenceScope[]
}

/**
 * Walk every app's section config in lookup order and return the first
 * match for `pathname`. Returns null when nothing matches — callers
 * decide their own fallback (the entity header renders an empty string,
 * `usePageTitle` falls back to a humanized URL segment).
 */
export function resolveSectionMatch(pathname: string): SectionMatch | null {
  for (const [config, prefix] of APP_SECTION_LOOKUP) {
    const { matched, params, route } = getMatchedSection(
      pathname,
      config,
      prefix
    )
    if (!matched) continue
    const title =
      typeof matched.title === 'function'
        ? matched.title(params)
        : matched.title
    const presenceScopes =
      matched.presence === false || !route
        ? []
        : deriveScopeChain(prefix, route, params)
    return { title, params, prefix, route: route ?? null, presenceScopes }
  }
  return null
}
